<script setup lang="ts">
    import { onMounted, onUnmounted, ref } from 'vue';
    import { useRouter } from 'vue-router';
    import { useAuthStore } from '@/store/auth';
    import { useFraudAlert } from '@/composables/useFraudAlert';
    import { LoaderCircle, TriangleAlert } from 'lucide-vue-next';

    const router = useRouter();
    const { isFraudAlertOpen, fraudMessage, showFraudAlert, dismissFraudAlert } = useFraudAlert();
    const authStore = useAuthStore();
    const isLoggingOut = ref(false);

    const handleFraudEvent = (event: Event) => {
        const detail = (event as CustomEvent).detail;
        showFraudAlert(detail.message);
    };

    onMounted(() => {
        window.addEventListener('fraud-alert', handleFraudEvent);
    });

    onUnmounted(() => {
        window.removeEventListener('fraud-alert', handleFraudEvent);
    });

    const handleLogout = async () => {
        if (isLoggingOut.value) return;
        isLoggingOut.value = true;
        try {
            await authStore.logout();
            dismissFraudAlert();
            router.push('/landing-page');
        } catch {
            dismissFraudAlert();
            router.push('/landing-page');
        }
    };
</script>

<template>
    <Teleport to="body">
        <div
            v-if="isFraudAlertOpen"
            class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60"
            @click.self="() => {}"
        >
            <div class="mx-4 w-full max-w-md rounded-xl bg-white p-8 shadow-2xl">
                <div class="mb-6 text-center">
                    <div
                        class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100"
                    >
                        <triangle-alert class="h-8 w-8 text-red-600" />
                    </div>
                    <h3 class="text-lg font-semibold text-gray-900">Account Alert</h3>
                </div>
                <p class="mb-8 text-center text-sm text-gray-600">
                    {{ fraudMessage }}
                </p>
                <div class="flex justify-center">
                    <base-button
                        size="lg"
                        class="w-full"
                        :disabled="isLoggingOut"
                        @click="handleLogout"
                    >
                        <loader-circle
                            v-if="isLoggingOut"
                            class="size-4 animate-spin"
                        />
                        <span v-else>{{ isLoggingOut ? 'Logging out...' : 'I understand' }}</span>
                    </base-button>
                </div>
            </div>
        </div>
    </Teleport>
</template>
